// Shared logic for the Torah, Haftarah, and Gospel portion pages.
// On a regular Shabbat, Hebcal returns a "parashat" item and we use the
// static data in torahPortionData.js. On festival Shabbatot (Sukkot, Pesach,
// Shavuot, etc.) there is no parashah, so we fall back to the holiday's
// leyning (torah / haftarah / maftir refs) that Hebcal provides.

import { getPortionByName } from "../torahPortionData";
import { fetchPassageHtml } from "./scripture";

const HEBCAL_URL = "https://www.hebcal.com/shabbat?cfg=json&M=on&leyning=on";

// Hebcal book names -> api.bible (USFM) book IDs
const BOOK_IDS = {
  "Genesis": "GEN", "Exodus": "EXO", "Leviticus": "LEV", "Numbers": "NUM", "Deuteronomy": "DEU",
  "Joshua": "JOS", "Judges": "JDG", "Ruth": "RUT",
  "I Samuel": "1SA", "II Samuel": "2SA", "1 Samuel": "1SA", "2 Samuel": "2SA",
  "I Kings": "1KI", "II Kings": "2KI", "1 Kings": "1KI", "2 Kings": "2KI",
  "I Chronicles": "1CH", "II Chronicles": "2CH", "1 Chronicles": "1CH", "2 Chronicles": "2CH",
  "Ezra": "EZR", "Nehemiah": "NEH", "Esther": "EST", "Job": "JOB", "Psalms": "PSA",
  "Proverbs": "PRO", "Ecclesiastes": "ECC", "Song of Songs": "SNG", "Isaiah": "ISA",
  "Jeremiah": "JER", "Lamentations": "LAM", "Ezekiel": "EZK", "Daniel": "DAN",
  "Hosea": "HOS", "Joel": "JOL", "Amos": "AMO", "Obadiah": "OBA", "Jonah": "JON",
  "Micah": "MIC", "Nahum": "NAM", "Habakkuk": "HAB", "Zephaniah": "ZEP", "Haggai": "HAG",
  "Zechariah": "ZEC", "Malachi": "MAL",
};

// Gospel/apostolic readings for festival Shabbatot, when there is no weekly portion to map from
const FESTIVAL_GOSPEL = [
  { match: /^Rosh Hashana/i,   nt: "1TH.4.13-1TH.5.11", passage: "1 Thessalonians 4:13-5:11", title: "Yom Teruah | \"The Last Trumpet\"" },
  { match: /^Yom Kippur/i,     nt: "HEB.9.1-HEB.9.28",  passage: "Hebrews 9:1-28",            title: "Yom HaKippurim | \"Our Great High Priest\"" },
  { match: /^Sukkot/i,         nt: "JHN.7.1-JHN.7.39",  passage: "John 7:1-39",               title: "Chag HaSukkot | \"Rivers of Living Water\"" },
  { match: /^Shmini Atzeret/i, nt: "JHN.7.37-JHN.8.12", passage: "John 7:37-8:12",            title: "Shemini Atzeret | \"The Light of the World\"" },
  { match: /^Simchat Torah/i,  nt: "JHN.1.1-JHN.1.18",  passage: "John 1:1-18",               title: "Simchat Torah | \"The Word Became Flesh\"" },
  { match: /^Pesach/i,         nt: "LUK.22.7-LUK.22.30", passage: "Luke 22:7-30",             title: "Pesach | \"This Is My Body\"" },
  { match: /^Shavuot/i,        nt: "ACT.2.1-ACT.2.41",  passage: "Acts 2:1-41",               title: "Shavuot | \"The Promise of the Spirit\"" },
  { match: /^Chanukah/i,       nt: "JHN.10.22-JHN.10.42", passage: "John 10:22-42",           title: "Chanukah | \"The Feast of Dedication\"" },
];

// Converts one Hebcal ref ("Leviticus 22:26-23:44", "Zechariah 14:1-21") into an
// api.bible passage ID. A part with no book name inherits the previous book.
function refToPassageId(ref, prevBook) {
  const m = ref.trim().match(/^(.*?)\s*(\d+):(\d+)(?:\s*-\s*(?:(\d+):)?(\d+))?$/);
  if (!m) return null;
  const [, bookName, ch, vs, endCh, endVs] = m;
  const book = bookName ? BOOK_IDS[bookName] : prevBook;
  if (!book) return null;
  const start = `${book}.${ch}.${vs}`;
  const id = endVs ? `${start}-${book}.${endCh || ch}.${endVs}` : start;
  return { id, book };
}

// "Deuteronomy 33:1-34:12; Genesis 1:1-2:3" -> ["DEU.33.1-DEU.34.12", "GEN.1.1-GEN.2.3"]
export function refsToPassageIds(refs) {
  if (!refs) return [];
  const ids = [];
  let prevBook = null;
  for (const part of refs.split(/[;,]/)) {
    const parsed = refToPassageId(part, prevBook);
    if (parsed) {
      ids.push(parsed.id);
      prevBook = parsed.book;
    }
  }
  return ids;
}

// Fetches one or more api.bible passages and joins their HTML
export async function fetchPassages(passageIds) {
  const parts = await Promise.all(
    passageIds.map(id =>
      fetchPassageHtml(id, { "content-type": "html", "include-verse-numbers": "true" })
    )
  );
  return parts.filter(Boolean).join("<br />") || "Text not available.";
}

function isSaturday(dateStr) {
  const [y, m, d] = (dateStr || "").slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).getDay() === 6;
}

async function loadWeeklyReading() {
  const res = await fetch(HEBCAL_URL);
  const data = await res.json();
  const items = data.items || [];

  const parashat = items.find(item => item.category === "parashat");
  if (parashat) {
    const name = parashat.title.replace("Parashat ", "");
    const portion = getPortionByName(name);
    const leyning = parashat.leyning || {};
    return {
      isFestival: false,
      name,
      hebrew: parashat.hebrew || "",
      torahRef: portion?.ref || leyning.torah || "",
      torahIds: portion ? [portion.torahApi] : refsToPassageIds(leyning.torah),
      haftarahRef: portion?.haftarahRef || leyning.haftarah || "",
      haftarahIds: portion ? [portion.haftarahApi] : refsToPassageIds(leyning.haftarah),
      maftirRef: "",
      maftirIds: [],
    };
  }

  // No parashah this week: use the festival reading, preferring the one on Shabbat itself
  const festivals = items.filter(item => item.category === "holiday" && item.leyning?.torah);
  const festival = festivals.find(item => isSaturday(item.date)) || festivals[festivals.length - 1];
  if (!festival) return null;

  const { torah, haftarah, maftir } = festival.leyning;
  return {
    isFestival: true,
    name: festival.title,
    hebrew: festival.hebrew || "",
    torahRef: torah,
    torahIds: refsToPassageIds(torah),
    haftarahRef: haftarah || "",
    haftarahIds: refsToPassageIds(haftarah),
    maftirRef: maftir || "",
    maftirIds: refsToPassageIds(maftir),
  };
}

// Shared across the three portion pages so Hebcal is only hit once per page load
let readingPromise = null;

export function getWeeklyReading() {
  if (!readingPromise) {
    readingPromise = loadWeeklyReading().catch(err => {
      readingPromise = null;
      throw err;
    });
  }
  return readingPromise;
}

export function getFestivalGospel(festivalName) {
  return FESTIVAL_GOSPEL.find(f => f.match.test(festivalName)) || null;
}
